<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useChatStore } from '../stores/chatStore';

const chatStore = useChatStore();
const { t } = useI18n();

const queue = computed(() => chatStore.followUpQueue);

const progressLabel = computed(() => {
  if (!chatStore.followUpTotalCount) {
    return '';
  }

  return `${t('followUpProgressLabel')} ${chatStore.followUpCompletedCount}/${chatStore.followUpTotalCount}`;
});

const statusText = (status: string) => {
  switch (status) {
    case 'running':
      return t('followUpStatusRunning');
    case 'success':
      return t('followUpStatusSuccess');
    case 'error':
      return t('followUpStatusError');
    default:
      return t('followUpStatusPending');
  }
};

const statusIcon = (status: string) => {
  switch (status) {
    case 'running':
      return 'material-symbols:autorenew';
    case 'success':
      return 'material-symbols:check-circle-rounded';
    case 'error':
      return 'material-symbols:error-outline-rounded';
    default:
      return 'material-symbols:schedule-rounded';
  }
};

const statusClass = (status: string) => {
  switch (status) {
    case 'running':
      return 'text-blue-600';
    case 'success':
      return 'text-green-600';
    case 'error':
      return 'text-red-600';
    default:
      return 'text-gray-500';
  }
};
</script>

<template>
  <div class="follow-up-status bg-white border border-gray-200 rounded-lg p-3 shadow-sm space-y-3">
    <div class="flex items-center justify-between">
      <p class="font-semibold text-sm" :class="chatStore.createLanguageClass(chatStore.language)">
        {{ t('followUpQueueHeading') }}
      </p>
      <span v-if="progressLabel" class="text-xs text-gray-500">
        {{ progressLabel }}
      </span>
    </div>

    <p v-if="!queue.length" class="text-xs text-gray-500" :class="chatStore.createLanguageClass(chatStore.language)">
      {{ t('followUpQueueEmpty') }}
    </p>

    <ul v-else class="space-y-2 max-h-40 overflow-y-auto pr-1">
      <li v-for="task in queue" :key="task.id"
        class="flex items-start gap-2 border border-gray-100 rounded-md px-2 py-1 bg-gray-50">
        <Icon :name="statusIcon(task.status)" size="18" :class="[statusClass(task.status), task.status === 'running' ? 'animate-spin' : '']" />
        <div class="flex-1 min-w-0">
          <p class="text-xs font-medium truncate" :class="chatStore.createLanguageClass(chatStore.language)">
            {{ task.question }}
          </p>
          <p class="text-[11px] mt-0.5" :class="['text-gray-500', chatStore.createLanguageClass(chatStore.language)]">
            {{ statusText(task.status) }}
          </p>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped>
@reference "../assets/css/tailwind.css";
</style>
